import { Package, Plus } from "@/components/icons";
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Separator,
} from "@/components/kit";
import type { MatchImportOrderLineDTO } from "@/modules/inventory/domain/import-order-contracts";
import type { ImportOrderPhase13LineDTO } from "@/modules/inventory/domain/types";
import {
  ProductPickerModal,
  type PickedVariant,
} from "@/modules/orders/presentation/ui/ProductPickerModal";
import type { ProductPickerItemDTO } from "@/modules/products/domain/types";
import { useProductCategoriesQuery } from "@/modules/products/presentation/api/queries";
import { normalizeStockUnitLabel } from "@/modules/products/domain/unit-conversion";
import { useEffect, useState } from "react";
import { importOrderPriceUnitLabel } from "../lib/import-order-labels";

type MatchMode = "pick" | "create";

type SelectedProductForVariant = {
  id: string;
  name: string;
};

type ImportOrderMatchDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  line: ImportOrderPhase13LineDTO | null;
  currency?: string;
  isSubmitting?: boolean;
  onConfirm: (body: MatchImportOrderLineDTO) => void | Promise<void>;
};

export function ImportOrderMatchDialog({
  open,
  onOpenChange,
  line,
  currency = "CNY",
  isSubmitting,
  onConfirm,
}: ImportOrderMatchDialogProps) {
  const categories = useProductCategoriesQuery();
  const [mode, setMode] = useState<MatchMode>("pick");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [picked, setPicked] = useState<PickedVariant | null>(null);
  const [selectedProduct, setSelectedProduct] =
    useState<SelectedProductForVariant | null>(null);
  const [categoryIds, setCategoryIds] = useState<string[]>([]);
  const [productName, setProductName] = useState("");
  const [variantName, setVariantName] = useState("ມາດຕະຐານ");
  const [stockUnitLabel, setStockUnitLabel] = useState("ຊິ້ນ");
  const [sku, setSku] = useState("");
  const [price, setPrice] = useState(0);

  useEffect(() => {
    if (!open || !line) return;
    setMode("pick");
    setPickerOpen(false);
    setPicked(null);
    setSelectedProduct(null);
    setProductName(line.externalTitle?.trim() || line.productName || "");
    setVariantName("ມາດຕະຐານ");
    setStockUnitLabel("ຊິ້ນ");
    setSku("");
    setPrice(0);
    const categoryList = categories.data?.data ?? [];
    const defaultCategory =
      categoryList.find((c) => c.isActive)?.id ?? categoryList[0]?.id ?? null;
    setCategoryIds(defaultCategory ? [defaultCategory] : []);
  }, [open, line, categories.data?.data]);

  const priceUnit = importOrderPriceUnitLabel(currency);
  const title = line?.externalTitle?.trim() || line?.productName || "—";

  const canSubmitPickVariant = picked != null;
  const canSubmitCreateVariantOnProduct =
    selectedProduct != null &&
    variantName.trim().length > 0 &&
    stockUnitLabel.trim().length > 0;
  const canSubmitPick = canSubmitPickVariant || canSubmitCreateVariantOnProduct;
  const canSubmitCreate =
    categoryIds.length > 0 &&
    productName.trim().length > 0 &&
    variantName.trim().length > 0 &&
    stockUnitLabel.trim().length > 0;

  function toggleCategory(id: string, checked: boolean) {
    setCategoryIds((prev) =>
      checked ? [...prev, id] : prev.filter((c) => c !== id),
    );
  }

  function handlePickVariant(variant: PickedVariant) {
    setPicked(variant);
    setSelectedProduct(null);
    setPickerOpen(false);
  }

  function handleSelectProductForVariant(product: ProductPickerItemDTO) {
    setSelectedProduct({ id: product.id, name: product.name });
    setPicked(null);
    const defaultVariantName =
      line?.externalTitle?.trim() ||
      product.variants[0]?.name ||
      "ມາດຕະຐານ";
    setVariantName(defaultVariantName);
    setStockUnitLabel(
      product.variants[0]?.stockUnitLabel?.trim() || "ຊິ້ນ",
    );
    setPrice(0);
    setPickerOpen(false);
  }

  async function handleConfirm() {
    if (!line) return;
    if (mode === "pick" && picked) {
      await onConfirm({ variantId: picked.variantId });
      return;
    }
    if (mode === "pick" && selectedProduct && canSubmitCreateVariantOnProduct) {
      await onConfirm({
        createVariant: {
          productId: selectedProduct.id,
          variantName: variantName.trim(),
          stockUnitLabel: normalizeStockUnitLabel(stockUnitLabel),
          sku: sku.trim() || null,
          price,
        },
      });
      return;
    }
    if (mode === "create" && canSubmitCreate) {
      await onConfirm({
        createProduct: {
          categoryIds,
          productName: productName.trim(),
          variantName: variantName.trim(),
          stockUnitLabel: normalizeStockUnitLabel(stockUnitLabel),
          sku: sku.trim() || null,
          price,
        },
      });
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="flex max-h-[min(90dvh,680px)] flex-col gap-4 sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              ຈັບຄູ່ SKU
            </DialogTitle>
          </DialogHeader>

          {line ? (
            <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
              <div className="rounded-xl border border-secondary/25 bg-secondary/5 px-3 py-2.5 text-sm">
                <p className="font-medium leading-snug">{title}</p>
                {line.externalUrl ? (
                  <a
                    href={line.externalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 inline-block text-secondary text-xs underline-offset-2 hover:underline"
                  >
                    ເປີດລິ້ງຈາກແອບ
                  </a>
                ) : null}
                <p className="mt-1 text-muted-foreground text-xs tabular-nums">
                  {line.orderedQuantity} ·{" "}
                  {Number(line.unitPriceForeign).toLocaleString()} {priceUnit}
                </p>
              </div>

              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={mode === "pick" ? "default" : "outline"}
                  size="sm"
                  className="flex-1 rounded-full"
                  onClick={() => setMode("pick")}
                >
                  ເລືອກຈາກລະບົບ
                </Button>
                <Button
                  type="button"
                  variant={mode === "create" ? "default" : "outline"}
                  size="sm"
                  className="flex-1 rounded-full"
                  onClick={() => setMode("create")}
                >
                  <Plus data-icon="inline-start" />
                  ສ້າງສິນຄ້າໃໝ່
                </Button>
              </div>

              {mode === "pick" ? (
                <div className="flex flex-col gap-3">
                  {picked ? (
                    <div className="flex items-start gap-3 rounded-xl border border-leaf/30 bg-leaf/8 p-3">
                      <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-secondary">
                        <Package className="size-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm">{picked.productName}</p>
                        <p className="text-muted-foreground text-xs">
                          {picked.variantName}
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="rounded-full"
                        onClick={() => setPickerOpen(true)}
                      >
                        ປ່ຽນ
                      </Button>
                    </div>
                  ) : selectedProduct ? (
                    <div className="flex flex-col gap-3">
                      <div className="flex items-start gap-3 rounded-xl border border-secondary/25 bg-secondary/5 p-3">
                        <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-secondary">
                          <Package className="size-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-sm">
                            {selectedProduct.name}
                          </p>
                          <p className="text-muted-foreground text-xs">
                            ສ້າງ variant ໃໝ່ໃນສິນຄ້ານີ້
                          </p>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="rounded-full"
                          onClick={() => setPickerOpen(true)}
                        >
                          ປ່ຽນ
                        </Button>
                      </div>
                      <div>
                        <Label htmlFor="match-pick-variant-name">
                          ຊື່ variant *
                        </Label>
                        <Input
                          id="match-pick-variant-name"
                          value={variantName}
                          onChange={(e) => setVariantName(e.target.value)}
                        />
                      </div>
                      <div>
                        <Label htmlFor="match-pick-sku">SKU (ທາງເລືອກ)</Label>
                        <Input
                          id="match-pick-sku"
                          value={sku}
                          onChange={(e) => setSku(e.target.value)}
                          placeholder="ປ້ອນ SKU ຖ້າມີ"
                        />
                      </div>
                      <p className="text-muted-foreground text-xs">
                        ວິທີຈັດສະຕັອກ: ສະຕັອກແຍກ (ຄ່າເລີ່ມຕົ້ນ)
                      </p>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div>
                          <Label htmlFor="match-pick-stock-unit">ຫົວໜ່ວຍ</Label>
                          <Input
                            id="match-pick-stock-unit"
                            value={stockUnitLabel}
                            onChange={(e) => setStockUnitLabel(e.target.value)}
                          />
                        </div>
                        <div>
                          <Label htmlFor="match-pick-price">ລາຄາຂາຍ (LAK)</Label>
                          <Input
                            id="match-pick-price"
                            type="number"
                            min={0}
                            value={price || ""}
                            onChange={(e) =>
                              setPrice(Math.max(0, Number(e.target.value) || 0))
                            }
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      className="rounded-full"
                      onClick={() => setPickerOpen(true)}
                    >
                      <Package data-icon="inline-start" />
                      ເລືອກສິນຄ້າ / variant
                    </Button>
                  )}
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  <div>
                    <Label className="text-xs">ໝວດໝູ່ *</Label>
                    <ul className="mt-2 flex max-h-32 flex-col gap-2 overflow-y-auto rounded-xl border p-3">
                      {(categories.data?.data ?? []).map((category) => (
                        <li key={category.id}>
                          <label className="flex cursor-pointer items-center gap-2 text-sm">
                            <Checkbox
                              checked={categoryIds.includes(category.id)}
                              onCheckedChange={(c) =>
                                toggleCategory(category.id, Boolean(c))
                              }
                            />
                            {category.name}
                          </label>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <Label htmlFor="match-product-name">ຊື່ສິນຄ້າ *</Label>
                    <Input
                      id="match-product-name"
                      value={productName}
                      onChange={(e) => setProductName(e.target.value)}
                    />
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="match-variant-name">ຊື່ variant</Label>
                      <Input
                        id="match-variant-name"
                        value={variantName}
                        onChange={(e) => setVariantName(e.target.value)}
                      />
                    </div>
                    <div>
                      <Label htmlFor="match-stock-unit">ຫົວໜ່ວຍ</Label>
                      <Input
                        id="match-stock-unit"
                        value={stockUnitLabel}
                        onChange={(e) => setStockUnitLabel(e.target.value)}
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="match-sku">SKU (ທາງເລືອກ)</Label>
                    <Input
                      id="match-sku"
                      value={sku}
                      onChange={(e) => setSku(e.target.value)}
                      placeholder="ປ້ອນ SKU ຖ້າມີ"
                    />
                  </div>
                  <p className="text-muted-foreground text-xs">
                    ວິທີຈັດສະຕັອກ: ສະຕັອກແຍກ (ຄ່າເລີ່ມຕົ້ນ)
                  </p>
                  <div>
                    <Label htmlFor="match-price">ລາຄາຂາຍ (LAK)</Label>
                    <Input
                      id="match-price"
                      type="number"
                      min={0}
                      value={price || ""}
                      onChange={(e) =>
                        setPrice(Math.max(0, Number(e.target.value) || 0))
                      }
                    />
                  </div>
                </div>
              )}
            </div>
          ) : null}

          <Separator />

          <DialogFooter className="gap-2 sm:justify-end">
            <Button
              variant="outline"
              className="rounded-full"
              onClick={() => onOpenChange(false)}
            >
              ຍົກເລີກ
            </Button>
            <Button
              className="rounded-full"
              disabled={
                !line ||
                isSubmitting ||
                (mode === "pick" ? !canSubmitPick : !canSubmitCreate)
              }
              isLoading={isSubmitting}
              onClick={handleConfirm}
            >
              ຢືນຢັນຈັບຄູ່
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ProductPickerModal
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onPick={handlePickVariant}
        onCreateVariant={handleSelectProductForVariant}
        createVariantLabel="ສ້າງ variant ໃໝ່ໃນສິນຄ້ານີ້"
        allowZeroStock
        purpose="receive"
        excludeBundles
        subtitle="ເລືອກ variant ທີ່ຕົງກັບສິນຄ້າຈາກແອບຈີນ"
      />
    </>
  );
}
